// third party
import { createSlice } from "@reduxjs/toolkit";

// project imports
import axios from "utils/axios";
import { dispatch } from "../index";

// ==============================|| SLICE - RALLY ||============================== //

const initialState = {
  error: null,
  rallies: [],
  currentRally: null,
  loading: false,
  rallyOfficerRallies: [],
  rallyBookings: [],
  rallyBookingsLoading: false,
};

const slice = createSlice({
  name: "rally",
  initialState,
  reducers: {
    // HAS ERROR
    hasError(state, action) {
      state.error = action.payload;
      state.loading = false;
    },

    // SET LOADING
    setLoading(state, action) {
      state.loading = action.payload;
      if (action.payload) state.error = null;
    },

    // GET RALLIES
    getRalliesSuccess(state, action) {
      state.error = null;
      state.rallies = action.payload;
      state.loading = false;
    },

    // GET SINGLE RALLY
    getRallySuccess(state, action) {
      state.error = null;
      state.currentRally = action.payload;
      state.loading = false;
    },

    // CREATE RALLY
    createRallySuccess(state, action) {
      state.error = null;
      state.rallies.push(action.payload);
      state.loading = false;
    },

    // UPDATE RALLY
    updateRallySuccess(state, action) {
      state.error = null;
      const index = state.rallies.findIndex(
        (rally) => rally.id === action.payload.id,
      );
      if (index !== -1) {
        state.rallies[index] = action.payload;
      }
      state.currentRally = action.payload;
      state.loading = false;
    },

    // DELETE RALLY
    deleteRallySuccess(state, action) {
      state.error = null;
      state.rallies = state.rallies.filter(
        (rally) => rally.id !== action.payload,
      );
      state.loading = false;
    },

    // CLEAR CURRENT RALLY
    clearCurrentRally(state) {
      state.currentRally = null;
    },

    // GET RALLY OFFICER RALLIES
    getRallyOfficerRalliesSuccess(state, action) {
      state.error = null;
      state.rallyOfficerRallies = action.payload;
      state.loading = false;
    },

    // GET RALLY BOOKINGS
    getRallyBookingsSuccess(state, action) {
      state.error = null;
      state.rallyBookings = action.payload;
      state.rallyBookingsLoading = false;
    },

    // SET RALLY BOOKINGS LOADING
    setRallyBookingsLoading(state, action) {
      state.rallyBookingsLoading = action.payload;
      if (action.payload) state.error = null;
    },
  },
});

// Reducer
export default slice.reducer;

// ==============================|| SLICE - RALLY ACTIONS ||============================== //

export function getRallies() {
  return async () => {
    try {
      dispatch(slice.actions.setLoading(true));
      const response = await axios.get("/rallies");
      dispatch(slice.actions.getRalliesSuccess(response.data.rallies));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getRallyById(rallyId) {
  return async () => {
    try {
      dispatch(slice.actions.setLoading(true));
      const response = await axios.get(`/rallies/${rallyId}`);
      dispatch(slice.actions.getRallySuccess(response.data.rally));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function createRally(rallyData) {
  return async () => {
    try {
      dispatch(slice.actions.setLoading(true));
      const response = await axios.post("/rallies", rallyData);
      dispatch(slice.actions.createRallySuccess(response.data.rally));
      return response.data.rally;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function updateRally(rallyId, rallyData) {
  return async () => {
    try {
      dispatch(slice.actions.setLoading(true));
      const response = await axios.put(`/rallies/${rallyId}`, rallyData);
      dispatch(slice.actions.updateRallySuccess(response.data.rally));
      return response.data.rally;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function deleteRally(rallyId) {
  return async () => {
    try {
      dispatch(slice.actions.setLoading(true));
      await axios.delete(`/rallies/${rallyId}`);
      dispatch(slice.actions.deleteRallySuccess(rallyId));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function clearCurrentRally() {
  return async () => {
    dispatch(slice.actions.clearCurrentRally());
  };
}

export function getRallyOfficerRallies() {
  return async () => {
    try {
      dispatch(slice.actions.setLoading(true));
      const response = await axios.get("/rally-officer/rallies");
      dispatch(
        slice.actions.getRallyOfficerRalliesSuccess(response.data.rallies),
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getRallyBookings(rallyId, showLoading = true) {
  return async () => {
    try {
      if (showLoading) dispatch(slice.actions.setRallyBookingsLoading(true));
      const response = await axios.get(
        `/rally-officer/rallies/${rallyId}/bookings`,
      );
      dispatch(slice.actions.getRallyBookingsSuccess(response.data.bookings));
    } catch (error) {
      if (showLoading) {
        dispatch(slice.actions.hasError(error));
        dispatch(slice.actions.setRallyBookingsLoading(false));
      } else {
        throw error;
      }
    }
  };
}

export function getRallyOfficerRallyById(rallyId) {
  return async () => {
    try {
      dispatch(slice.actions.setLoading(true));
      const response = await axios.get(`/rally-officer/rallies/${rallyId}`);
      dispatch(slice.actions.getRallySuccess(response.data.rally));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function updateRallyOfficerDetails(rallyId, details) {
  return async () => {
    const response = await axios.patch(`/rally-officer/rallies/${rallyId}`, details);
    dispatch(slice.actions.getRallySuccess(response.data.rally));
    return response.data.rally;
  };
}

export function updateBookingStatus(
  rallyId,
  bookingId,
  status,
  notes = "",
  officerId = null,
) {
  return async () => {
    try {
      const response = await axios.put(
        `/rally-officer/rallies/${rallyId}/bookings/${bookingId}/status`,
        {
          status,
          notes,
          officer_id: officerId,
        },
      );
      // Refresh bookings after status update
      dispatch(getRallyBookings(rallyId));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function updateBooking(
  rallyId,
  bookingId,
  bookingData,
  officerId = null,
) {
  return async () => {
    try {
      const response = await axios.put(
        `/rally-officer/rallies/${rallyId}/bookings/${bookingId}`,
        {
          ...bookingData,
          officer_id: officerId,
        },
      );
      // Refresh bookings after update
      dispatch(getRallyBookings(rallyId));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function emailAttendees(
  rallyId,
  message,
  officerId = null,
  bookingIds = null,
) {
  return async () => {
    try {
      const response = await axios.post(
        `/rally-officer/rallies/${rallyId}/email-attendees`,
        {
          message,
          officer_id: officerId,
          booking_ids: bookingIds,
        },
      );
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function addPayment(
  rallyId,
  bookingId,
  amount,
  note = "",
  paymentMethod = null,
  bookingName = null,
) {
  return async () => {
    const response = await axios.post(
      `/rally-officer/rallies/${rallyId}/bookings/${bookingId}/payments`,
      {
        amount,
        note,
        payment_method: paymentMethod,
        ...(bookingName !== null && { full_name: bookingName }),
      },
    );
    return response.data;
  };
}

export function getRallyOfficerBookingsAggregated(bookingType, showLoading = true) {
  return async () => {
    try {
      if (showLoading) dispatch(slice.actions.setRallyBookingsLoading(true));
      const response = await axios.get(
        `/rally-officer/bookings?type=${bookingType}`,
      );
      dispatch(slice.actions.getRallyBookingsSuccess(response.data.bookings));
      return response.data.bookings;
    } catch (error) {
      if (showLoading) {
        dispatch(slice.actions.hasError(error));
        dispatch(slice.actions.setRallyBookingsLoading(false));
      }
      throw error;
    }
  };
}

// ==============================|| ADMIN RALLY ACTIONS ||============================== //

export function getAdminRallyById(rallyId) {
  return async () => {
    try {
      dispatch(slice.actions.setLoading(true));
      const response = await axios.get(`/rallies/${rallyId}`);
      dispatch(slice.actions.getRallySuccess(response.data.rally));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getAdminRallyBookings(rallyId, showLoading = true) {
  return async () => {
    try {
      if (showLoading) dispatch(slice.actions.setRallyBookingsLoading(true));
      const response = await axios.get(`/rallies/${rallyId}/bookings`);
      dispatch(slice.actions.getRallyBookingsSuccess(response.data.bookings));
    } catch (error) {
      if (showLoading) {
        dispatch(slice.actions.hasError(error));
        dispatch(slice.actions.setRallyBookingsLoading(false));
      } else {
        throw error;
      }
    }
  };
}

export function updateAdminBookingStatus(
  rallyId,
  bookingId,
  status,
  notes = "",
) {
  return async () => {
    try {
      const response = await axios.put(
        `/rallies/${rallyId}/bookings/${bookingId}/status`,
        {
          status,
          notes,
        },
      );
      // Refresh bookings after status update
      dispatch(getAdminRallyBookings(rallyId));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function addAdminBookingPayment(
  rallyId,
  bookingId,
  amount,
  note = "",
  paymentMethod = null,
  bookingName = null,
) {
  return async () => {
    const response = await axios.post(
      `/rallies/${rallyId}/bookings/${bookingId}/payments`,
      {
        amount,
        note,
        payment_method: paymentMethod,
        ...(bookingName !== null && { full_name: bookingName }),
      },
    );
    return response.data;
  };
}

export function emailAdminAttendees(rallyId, message, bookingIds = null) {
  return async () => {
    try {
      const response = await axios.post(`/rallies/${rallyId}/email-attendees`, {
        message,
        booking_ids: bookingIds,
      });
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function updateAdminBooking(rallyId, bookingId, bookingData) {
  return async () => {
    try {
      const response = await axios.put(
        `/rallies/${rallyId}/bookings/${bookingId}`,
        bookingData,
      );
      // Refresh bookings after update
      dispatch(getAdminRallyBookings(rallyId));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function setRallyPricing(rallyId, pricingData) {
  return async () => {
    try {
      const response = await axios.post(
        `/rally-officer/rallies/${rallyId}/set-pricing`,
        pricingData,
      );
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function uploadRallyImage(rallyId, file) {
  return async () => {
    try {
      dispatch(slice.actions.setLoading(true));
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`/rallies/${rallyId}/image`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      dispatch(slice.actions.updateRallySuccess(response.data.rally));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function deleteRallyImage(rallyId) {
  return async () => {
    try {
      dispatch(slice.actions.setLoading(true));
      const response = await axios.delete(`/rallies/${rallyId}/image`);
      dispatch(slice.actions.updateRallySuccess(response.data.rally));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function uploadRallyFlyer(rallyId, file) {
  return async () => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`/rallies/${rallyId}/flyer`, formData);
    dispatch(slice.actions.updateRallySuccess(response.data.rally));
    return response.data;
  };
}

export function deleteRallyFlyer(rallyId) {
  return async () => {
    const response = await axios.delete(`/rallies/${rallyId}/flyer`);
    dispatch(slice.actions.updateRallySuccess(response.data.rally));
    return response.data;
  };
}
